/**
 * V2 rebuild against Figma frame a_start_language_selection (393×852).
 * Functional contract preserved: public-run fetch, wizard state init,
 * language-change-before-navigate, navigation target branching.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from 'react-router';
import { useTranslation, Trans } from 'react-i18next';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import OnboardingShellV2 from '../components/OnboardingShellV2';
import ProgressIndicatorV2 from '../components/ProgressIndicatorV2';
import LanguageDropdown from '../components/LanguageDropdown';
import heroWelcome from '../assets/onboarding/hero-language.svg';
import { pillButtonFocusRing } from '../styles/onboardingButtons';
import {
  resolveDefaultLanguage,
  type SupportedLanguage,
} from '../i18n/resolveDefaultLanguage';
import { fetchByCode, type PublicRun } from '../services/publicRuns';
import { useWizardState } from '../wizard/useWizardState';
import { getStepInfo } from '../wizard/wizardSteps';

const HELPER_TEXT_ID = 'welcome-language-helper';

type RallyeFetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; run: PublicRun }
  | { status: 'error' };

const WelcomeStep: React.FC = () => {
  const { t, i18n } = useTranslation('welcome');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { code: runCode } = useParams<{ code?: string }>();
  const wizard = useWizardState();

  const initialLang = useMemo<SupportedLanguage>(
    () => resolveDefaultLanguage(searchParams.get('lang')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [lang, setLang] = useState<SupportedLanguage>(initialLang);

  const [rallyeState, setRallyeState] = useState<RallyeFetchState>({
    status: runCode ? 'loading' : 'idle',
  });

  useEffect(() => {
    if (!runCode) {
      setRallyeState({ status: 'idle' });
      return;
    }
    // Set the routing-relevant wizard state up front — independent of the
    // public-run lookup, which only supplies the display name. Without this,
    // a fast click through Welcome reaches EntryChoice/Profile before the
    // fetch resolves and the wizard has no runCode/pathKind to route on.
    wizard.setRunCode(runCode);
    wizard.setPathKind('with-code');
    let cancelled = false;
    setRallyeState({ status: 'loading' });
    fetchByCode(runCode).then((run) => {
      if (cancelled) return;
      if (run) {
        setRallyeState({ status: 'loaded', run });
        if (
          wizard.teamFlow.teamId !== null &&
          wizard.teamFlow.runId !== null &&
          wizard.teamFlow.runId !== run.run_id
        ) {
          wizard.resetTeamFlow();
        }
        if (run.organization_slug) {
          wizard.setOrganizationSlug(run.organization_slug);
        }
      } else setRallyeState({ status: 'error' });
    });
    return () => {
      cancelled = true;
    };
    // Intentionally depend only on `runCode`. The `wizard` context value is a
    // fresh object on every state update, so including it would re-run this
    // effect on every render. Same pattern as ProfileSetupStep / FindTeamStep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runCode]);

  // Apply the language change immediately when the user picks a different
  // entry in the dropdown — the surrounding UI (including this page's own
  // labels) re-renders in the new language right away, so the user sees the
  // effect before clicking Continue.
  const onLanguageChange = (next: SupportedLanguage) => {
    setLang(next);
    void i18n.changeLanguage(next);
  };

  const onContinue = () => {
    const target = runCode ? `/join/${runCode}/auth-choice` : '/auth-choice';
    navigate({ pathname: target, search: location.search });
  };

  const step = getStepInfo('welcome', runCode ? 'with-code' : 'no-code');

  const rallyeSubtitle = (() => {
    if (rallyeState.status === 'loading') {
      return <Skeleton variant="text" width="60%" aria-label={t('rallyeLoading')} />;
    }
    if (rallyeState.status === 'loaded') {
      return (
        <Typography
          sx={{
            fontFamily: "'Noto Sans', sans-serif",
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            letterSpacing: '0.10px',
            color: 'text.primary',
            // bbox-height drift (figma-implement skill §6 fourth-drift):
            // Figma rallye-subtitle bbox h=15, CSS lh=20 → CSS box is 5 px
            // taller. Neutralize the forward push so every sibling below
            // doesn't drift +5.
            marginBlockEnd: '-5px',
          }}
        >
          {t('rallyePrefix')}
          {rallyeState.run.rallye_name}
        </Typography>
      );
    }
    return null;
  })();

  return (
    <OnboardingShellV2
      contentSx={{ paddingInline: 0 }}
      progress={
        <ProgressIndicatorV2
          current={step.current}
          total={step.total}
        />
      }
    >
      <Box
        sx={{
          backgroundColor: 'var(--color-primary-95, #E6E9F4)',
          width: '100%',
          aspectRatio: '393 / 280',
          display: 'flex',
          alignItems: 'flex-end',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={heroWelcome}
          alt=""
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          paddingInline: '40px',
          paddingBlockStart: '24px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: "'Roboto', sans-serif",
              fontSize: '24px',
              fontWeight: 600,
              lineHeight: '32px',
              color: 'text.primary',
              margin: 0,
              marginBlockStart: '-2px',
            }}
          >
            <Trans
              t={t}
              i18nKey="headline"
              components={{
                brand: <Box component="span" sx={{ color: 'primary.main' }} />,
              }}
            />
          </Typography>
          {rallyeSubtitle}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              color: 'text.primary',
              marginBlockStart: '-3px',
              // bbox-height drift (figma-implement skill §6 fourth-drift):
              // Figma prompt bbox h=28 (body/large in Group 26 wrapper), CSS
              // lh=24 → CSS box 4 px shorter. Extend block bottom so next
              // sibling (dropdown wrapper) lands at Figma's y=423.
              paddingBlockEnd: '4px',
            }}
          >
            {t('selectLanguagePrompt')}
          </Typography>
          {/*
            Half-leading comp: the prompt above is body/large (lh 24, font ~18)
            with 3 px of CSS line-box leading per side that Figma's tight text
            bbox doesn't carry. Pulling the prompt up by 3 px lands its glyph
            on Figma's Y; this wrapper pushes the dropdown back down by 3 px
            so the dropdown — and everything below it (helper, Continue) —
            stays at its original absolute Y. See SKILL.md "Half-leading
            sub-drift within multi-line text elements".
          */}
          <Box sx={{ marginBlockStart: '3px', paddingBlockEnd: '4px' }}>
            <LanguageDropdown
              value={lang}
              onChange={onLanguageChange}
              label={t('languageLabel')}
              describedById={HELPER_TEXT_ID}
              options={[
                { code: 'de', label: t('langOptionDe') },
                { code: 'en', label: t('langOptionEn') },
              ]}
            />
          </Box>
        </Box>
        <Typography
          id={HELPER_TEXT_ID}
          sx={{
            fontFamily: "'Noto Sans', sans-serif",
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '16px',
            letterSpacing: '0.50px',
            color: '#000000',
          }}
        >
          {t('helperLanguageChangeable')}
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={onContinue}
          sx={{
            all: 'unset',
            boxSizing: 'border-box',
            cursor: 'pointer',
            alignSelf: 'flex-start',
            minWidth: '89px',
            minHeight: '40px',
            paddingInline: '16px',
            borderRadius: '100px',
            backgroundColor: '#4D69B9',
            color: '#FFFFFF',
            fontFamily: "'Roboto', sans-serif",
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            letterSpacing: '0.1px',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': { backgroundColor: '#3D54A0' },
            ...pillButtonFocusRing,
          }}
        >
          {t('continue')}
        </Box>
      </Box>
    </OnboardingShellV2>
  );
};

export default WelcomeStep;
